<template>
  <div class="mycases create-case">
    <div class="card">
      <div class="card-header">
        <h3>{{ $t("caseRecalculation") }}</h3>
      </div>
      <div class="card-body">
        <section id="2d-to-3d-cards">
          <!-- LOWER JAW CONTROLS -->
          <div
            v-if="(!biteScanned && isSelectedJaw === 'lower') || (biteScanned)"
            class="d-flex align-items-center mb-1 gap-2 d-modal-meta"
          >
            <div class="d-flex align-items-center gap-1">
              <label>{{ $t("selectSize") }}:</label>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                v-model="lowerSize"
              />
            </div>
            <button class="btn btn-primary" @click="rotateBtn('lower')">
              <span>{{ $t("rotate") }}</span>
            </button>
            <button class="btn btn-primary" @click="translateBtn('lower')">
              <span>{{ $t("translate") }}</span>
            </button>
            <div class="d-flex align-items-center gap-1">
              <label for="sensitivityInput"
                >{{ $t("controlSensitivity") }}:</label
              >
              <input
                type="range"
                min="0.1"
                max="5"
                step="0.1"
                v-model="sensitivity"
              />
            </div>
            <!-- Radio controls -->
            <div class="checkbox-group">
              <input
                type="radio"
                v-model="selectedControl"
                name="control-selection"
                value="orbit"
                class="checkbox-input"
                @change="switchControls"
                id="orbit"
              />
              <label for="orbit" class="checkbox-label">{{
                $t("orbit")
              }}</label>
            </div>
            <div class="checkbox-group">
              <input
                type="radio"
                class="checkbox-input"
                id="trackball"
                v-model="selectedControl"
                name="control-selection"
                value="trackball"
                @change="switchControls"
              />
              <label for="trackball" class="checkbox-label">{{
                $t("trackball")
              }}</label>
            </div>
            <div class="checkbox-group">
              <input
                type="radio"
                class="checkbox-input"
                id="fly"
                v-model="selectedControl"
                name="control-selection"
                value="fly"
                @change="switchControls"
              />
              <label for="fly" class="checkbox-label">{{ $t("fly") }}</label>
            </div>
          </div>

          <!-- UPPER JAW CONTROLS -->
          <div
            v-if="!biteScanned && isSelectedJaw === 'upper'"
            class="d-flex align-items-center mb-1 gap-2 d-modal-meta"
          >
            <div class="d-flex align-items-center gap-1">
              <label>{{ $t("selectSize") }}:</label>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                v-model="upperSize"
              />
            </div>
            <button class="btn btn-primary" @click="rotateBtn('upper')">
              <span>{{ $t("rotate") }}</span>
            </button>
            <button class="btn btn-primary" @click="translateBtn('upper')">
              <span>{{ $t("translate") }}</span>
            </button>
            <div class="d-flex align-items-center gap-1">
              <label for="sensitivityInputLower">
                {{ $t("controlSensitivity") }}:
              </label>
              <input
                type="range"
                min="0.1"
                max="5"
                step="0.1"
                v-model="sensitivityUpper"
              />
            </div>
            <!-- Radio controls -->
            <div class="checkbox-group">
              <input
                type="radio"
                v-model="selectedControlUpper"
                name="control-selection-uppers"
                value="orbit"
                class="checkbox-input"
                @change="switchControlsUpper"
                id="orbitUpper"
              />
              <label for="orbitUpper" class="checkbox-label">{{
                $t("orbit")
              }}</label>
            </div>
            <div class="checkbox-group">
              <input
                type="radio"
                class="checkbox-input"
                id="trackballUpper"
                v-model="selectedControlUpper"
                name="control-selection-upper"
                value="trackball"
                @change="switchControlsUpper"
              />
              <label for="trackballUpper" class="checkbox-label">
                {{ $t("trackball") }}
              </label>
            </div>
            <div class="checkbox-group">
              <input
                type="radio"
                class="checkbox-input"
                id="flyUpper"
                v-model="selectedControlUpper"
                name="control-selection-upper"
                value="fly"
                @change="switchControlsUpper"
              />
              <label for="flyUpper" class="checkbox-label">{{
                $t("fly")
              }}</label>
            </div>
          </div>

          <!-- TWO-CANVAS LAYOUT -->
          <b-row>
            <b-col md="6">
              <div class="d-upload-dropzone cursor-pointer">
                <!-- LOWER JAW CANVAS -->
                <div
                  class="lower-card"
                  :class="{ active: biteScanned || isSelectedJaw === 'lower' }"
                  :style="{ pointerEvents: (biteScanned || isSelectedJaw === 'lower') ? 'auto' : 'none' }"
                  ref="card"
                >
                  <canvas
                    id="canvas"
                    class="upper-canvas"
                    v-show="biteScanned || isSelectedJaw === 'lower'"
                  ></canvas>
                </div>

                <!-- UPPER JAW CANVAS -->
                <div
                  class="upper-card"
                  ref="cardUpper"
                  :style="{
                    pointerEvents: (!biteScanned && isSelectedJaw === 'upper') ? 'auto' : 'none',
                    zIndex: (!biteScanned && isSelectedJaw === 'upper') ? 1 : 0
                  }"
                >
                  <canvas
                    id="canvasUpper"
                    class="upper-canvas"
                    v-show="!biteScanned && isSelectedJaw === 'upper'"
                  ></canvas>
                </div>

                <div
                  v-if="!biteScanned"
                  class="dropzone-check"
                  :class="isSelectedJaw ? 'active' : ''"
                >
                  <div v-if="upperFileId || upperJawFile" class="checkbox-group">
                    <input
                      type="radio"
                      class="checkbox-input"
                      id="upper"
                      v-model="isSelectedJaw"
                      name="jaw-selection"
                      value="upper"
                      :checked="isSelectedJaw === 'upper'"
                    />
                    <label for="upper" class="checkbox-label">
                      {{ $t("showUpperJaw") }}
                    </label>
                  </div>
                  <div v-if="lowerFileId || lowerJawFile" class="checkbox-group">
                    <input
                      type="radio"
                      class="checkbox-input"
                      id="lower"
                      v-model="isSelectedJaw"
                      name="jaw-selection"
                      value="lower"
                      :checked="isSelectedJaw === 'lower'"
                    />
                    <label for="lower" class="checkbox-label">
                      {{ $t("showLowerJaw") }}
                    </label>
                  </div>
                </div>
              </div>
            </b-col>

            <b-col v-if="!isShow" md="3">
              <h3 class="mb-1">{{ $t("uploadInputs") }}</h3>
              <div v-if="upperFileId" class="mb-1">
                <h4>{{ $t("upperJaw") }}</h4>
                <div class="jaws-dropdown">
                  <div
                    class="custom-dropdzone"
                    @dragover.prevent="onDragOver"
                    @dragleave="onDragLeave"
                    @drop.prevent="onDrop($event, 'upper')"
                    :class="{ 'is-dragover': isDragOver }"
                  >
                    <div v-if="!upperJawFile" class="dropzone-content">
                      <div class="icon">
                        <ElementIcon icon="uploadIcon" />
                      </div>
                      <div>
                        <h3>{{ $t("selectOrDropFile") }}</h3>
                        <p>{{ $t("supportedModelFormats100MB") }}</p>
                      </div>
                    </div>
                    <div
                      v-else
                      class="uploaded-file-name d-flex align-items-center"
                    >
                      <p class="mr-2">
                        {{ $t("uploadedUpperJaw") }}: {{ upperJawFile.name }}
                      </p>
                      <feather-icon
                        class="cursor-pointer"
                        @click="deleteUpperJawFile()"
                        size="16"
                        icon="TrashIcon"
                      />
                    </div>
                    <feather-icon
                      v-if="downloadUpper"
                      icon="DownloadIcon"
                      class="cursor-pointer"
                      size="25"
                      @click="downloadUpperJaw"
                    />
                  </div>
                  <input
                    type="file"
                    ref="fileUpperJawInput"
                    @change="previewJaw('upper', $event)"
                    accept=".obj, .stl, .ply"
                    style="display: none"
                  />
                  <button
                    class="btn btn-secondary"
                    @click="openUpperJawFilePicker"
                  >
                    <ElementIcon class="stroke-icon" icon="folderIcon" />
                    <span>{{ $t("browse") }}</span>
                  </button>
                </div>
              </div>

              <h2 v-if="lowerFileId && upperFileId" class="mb-1">
                {{ $t("and") }} / {{ $t("or") }}
              </h2>

              <div v-if="lowerFileId" class="mb-1">
                <h4>{{ $t("lowerJaw") }}</h4>
                <div class="jaws-dropdown">
                  <div
                    class="custom-dropdzone"
                    @dragover.prevent="onDragOver"
                    @dragleave="onDragLeave"
                    @drop.prevent="onDrop($event, 'lower')"
                    :class="{ 'is-dragover': isDragOver }"
                  >
                    <div v-if="!lowerJawFile" class="dropzone-content">
                      <div class="icon">
                        <ElementIcon icon="uploadIcon" />
                      </div>
                      <div>
                        <h3>{{ $t("selectOrDropFile") }}</h3>
                        <p>{{ $t("supportedModelFormats100MB") }}</p>
                      </div>
                    </div>
                    <div
                      v-else
                      class="uploaded-file-name d-flex align-items-center"
                    >
                      <p class="mr-2">
                        {{ $t("uploadedLowerJaw") }}: {{ lowerJawFile.name }}
                      </p>
                      <feather-icon
                        class="cursor-pointer"
                        @click="deleteLowerJawFile"
                        size="16"
                        icon="TrashIcon"
                      />
                    </div>
                    <feather-icon
                      v-if="downloadLower"
                      icon="DownloadIcon"
                      class="cursor-pointer"
                      @click="downloadLowerJaw"
                      size="25"
                    />
                  </div>
                  <input
                    type="file"
                    ref="fileLowerJawInput"
                    @change="previewJaw('lower', $event)"
                    accept=".obj, .stl, .ply"
                    style="display: none"
                  />
                  <button
                    class="btn btn-secondary"
                    @click="openLowerJawFilePicker"
                  >
                    <ElementIcon icon="folderIcon" />
                    <span>{{ $t("browse") }}</span>
                  </button>
                </div>
              </div>
            </b-col>
            <b-col v-if="isShow" md="1"></b-col>
            <!-- RIGHT COLUMN: JAW FILE UPLOADS, ETC. -->
            <b-col md="3">
              <TeethSelector
                :modelValue="selectedTeeth"
                :toggledPairs="toggledTeeth"
                :palette="palette"
                @update:modelValue="selectedTeeth = $event"
              />
              <!-- <div v-if="productName == 'Shell Temp Design'" class="mb-1">
                <h4>{{ $t("uploadTeeth") }}</h4>
                <div
                  class="d-flex flex-md-row flex-column align-items-center justify-content-center gap-2"
                >
                  <div
                    class="custom-dropdzone"
                    @dragover.prevent="onDragOver"
                    @dragleave="onDragLeave"
                    @drop.prevent="onDrop($event, 'teeth')"
                    :class="{ 'is-dragover': isDragOver }"
                  >
                    <div class="dropzone-content">
                      <div class="icon">
                        <ElementIcon icon="uploadIcon" />
                      </div>
                      <div>
                        <h3>
                          {{ $t("selectAFileOrDragAndDropHere") }}
                        </h3>
                        <p>{{ $t("supportedTextFormats50MB") }}</p>
                      </div>
                    </div>
                  </div>
                  <input
                    type="file"
                    ref="fileTeethInput"
                    @change="previewTeeth"
                    style="display: none"
                  />
                  <button
                    class="btn btn-secondary"
                    @click="openTeethFilePicker"
                  >
                    <ElementIcon icon="folderIcon" />
                    <span>{{ $t("browse") }}</span>
                  </button>
                </div>
              </div> -->
              <div v-if="teethFile" class="d-flex justify-content-center mt-1">
                <b-button variant="primary" @click="downloadTeethFile">
                  <span>{{ $t("downloadTeethFile") }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </section>
        <div v-if="!isShow" class="d-flex justify-content-end mt-2">
          <b-button
            variant="primary"
            @click="updateCase"
            class="d-flex align-items-center gap-1"
            v-b-tooltip.hover
          >
            <feather-icon icon="PlusIcon" size="12" />
            <span>{{ $t("updateRecalculateCase") }}</span>
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CookieButton from "@/components/elements/CookieButton.vue";
import {
  BRow,
  BCol,
  BFormInput,
  BFormRadio,
  BCard,
  BCardText,
  BCardTitle,
  BImg,
  BFormFile,
  BFormGroup,
  BButton,
  BModal,
} from "bootstrap-vue";
import * as THREE from "three";
import NotificationService from "@/services/notification.service";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls.js";
import { FlyControls } from "three/examples/jsm/controls/FlyControls.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import FileService from "@/services/file.service";
import TextInput from "@/components/TextInput.vue";
import TextareaInput from "@/components/TextareaInput.vue";
import ElementIcon from "@/components/elements/Icon.vue";
import { mapGetters } from "vuex";
import { addLights, loadModel } from "@/utils/3d";
import TeethSelector from "@/components/TeethSelector.vue";

const IDENTITY = new THREE.Matrix4().toArray();

export default {
  name: "OptimizedUploader",
  components: {
    BRow,
    BCol,
    BFormRadio,
    BCard,
    BCardText,
    BCardTitle,
    BImg,
    BFormFile,
    BFormGroup,
    BButton,
    BModal,
    TextInput,
    TextareaInput,
    ElementIcon,
    CookieButton,
    TeethSelector,
  },
  computed: {
    ...mapGetters(["showLoader"]),
    palette() {
      const allPalette = [
        {
          color: "#691AFF",
          bgColor: "#CDB2FF",
          vectorBg: "#EEE5FF",
          text: "Preparation",
          reconstructionType: "ProvisionalCrown",
        },
        {
          color: "#B471A1",
          bgColor: "#D6B2CC",
          vectorBg: "#E8D4E2",
          text: "Extraction",
          reconstructionType: "ProvisionalPontic",
        },
        {
          color: "#730E1C",
          bgColor: "#C89DA3",
          vectorBg: "#DABEC2",
          text: "Pontic",
          reconstructionType: "AnatomicPontic",
        },
      ];
      if (this.productName === "dentaBridge") {
        return [
          ...allPalette.filter((item) => item.reconstructionType !== "ProvisionalPontic").map(item => item.reconstructionType === 'ProvisionalCrown'
            ? { ...item, text: 'Crown' }
            : item),
          {
            color: "#E08A00",
            bgColor: "#F5C97A",
            vectorBg: "#FDE8BF",
            text: "Onlay",
            reconstructionType: "Onlay",
          },
          {
            color: "#2E9E4F",
            bgColor: "#8FD4A5",
            vectorBg: "#C8EDD4",
            text: "Veneer",
            reconstructionType: "Veneer",
          },
          {
            color: "#1A7FD4",
            bgColor: "#7DC0F0",
            vectorBg: "#C0DFF8",
            text: "Inlay",
            reconstructionType: "Inlay",
          },
        ];
      }
      return allPalette;
    },
  },
  emits: ["updateCase"],
  props: {
    biteScanned: {
      type: Boolean,
      default: false,
    },
    upperFileId: {
      type: String,
      default: null,
    },
    lowerFileId: {
      type: String,
      default: null,
    },
    downloadUpper: {
      type: String,
      default: null,
    },
    downloadLower: {
      type: String,
      default: null,
    },
    isShow: {
      type: Boolean,
      default: false,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    isFormInvalid: {
      type: Boolean,
      default: false,
    },
    upperMatrixIn: {
      type: Array,
      default: () => IDENTITY,
    },
    upperMatrixWorldIn: {
      type: Array,
      default: () => IDENTITY,
    },
    lowerMatrixIn: {
      type: Array,
      default: () => IDENTITY,
    },
    lowerMatrixWorldIn: {
      type: Array,
      default: () => IDENTITY,
    },
    toggledPairs: {
      type: Array,
    },
    markedTeeth: {
      type: Array,
    },
    productName: {
      type: String,
      default: null,
    },
    teethFile: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
        // bite-mode helpers
      _biteGroup: null,
      _startUpperMatrix: null,
      _startLowerMatrix: null,

      _rafLower: null,
      _rafUpper: null,

      // Keep only small/primitive data in reactivity
      caseName: "",
      caseDetails: "",
      credits: 0,
      showModal: false,
      lowerJawFile: null,
      upperJawFile: null,

      // Transform data
      upperJawFileRotation: { orig: {}, reg: {} },
      lowerJawFileRotation: { orig: {}, reg: {} },
      upperJawFileTranslation: { orig: {}, reg: {} },
      lowerJawFileTranslation: { orig: {}, reg: {} },
      upperJawFileMatrixWorld: { orig: [], reg: [] },
      lowerJawFileMatrixWorld: { orig: [], reg: [] },
      upperJawFileQuaternion: { orig: {}, reg: {} },
      lowerJawFileQuaternion: { orig: {}, reg: {} },
      upperJawFileScale: { orig: {}, reg: {} },
      lowerJawFileScale: { orig: {}, reg: {} },
      upperJawFileMatrix: { orig: [], reg: [] },
      lowerJawFileMatrix: { orig: [], reg: [] },

      isSelectedJaw: "",
      isDragOver: false,
      isErrorMessage: false,
      termsAndConditions: false,

      // Sliders
      sensitivity: 2,
      sensitivityUpper: 1,
      selectedControl: "trackball",
      selectedControlUpper: "trackball",
      upperSize: 1,
      lowerSize: 1,
      selectedTeeth: [],
      toggledTeeth: [],
    };
  },
  watch: {
    toggledPairs: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.toggledTeeth = [...newVal];
        }
      },
      immediate: true,
    },
    markedTeeth: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.selectedTeeth = [...newVal];
        }
      },
      immediate: true,
    },
    upperSize(val) {
      if (this._upperGroundTruthModel) {
        this._upperGroundTruthModel.scale.setScalar(val);
      }
    },
    lowerSize(val) {
      if (this._lowerGroundTruthModel) {
        this._lowerGroundTruthModel.scale.setScalar(val);
      }
    },
    // Sensitivity watchers
    sensitivity(val) {
      this._updateControlsSensitivity(
        {
          orbitControls: this._orbitControls,
          trackballControls: this._trackballControls,
          flyControls: this._flyControls,
        },
        parseFloat(val)
      );
    },
    sensitivityUpper(val) {
      this._updateControlsSensitivity(
        {
          orbitControls: this._orbitControlsUpper,
          trackballControls: this._trackballControlsUpper,
          flyControls: this._flyControlsUpper,
        },
        parseFloat(val)
      );
    },
  },
  async mounted() {
    this.$store.commit("showLoader", true);
    try {
      // 2) Initialize the TWO Scenes
      this._initLowerScene();
      this._initUpperScene();

      // 3) Start animation loops
      this._clock = new THREE.Clock();
      this._clockUpper = new THREE.Clock();
      this._animateLower();
      this._animateUpper();

      await this._loadFiles();

      // 5) Listen to window events
      window.addEventListener("resize", this._onWindowResizeLower);
      window.addEventListener("resize", this._onWindowResizeUpper);
      window.addEventListener("keydown", this._handleKeyDown);
      window.addEventListener("keyup", this._handleKeyUp);

      if (!this.isSelectedJaw) {
        if (this.lowerFileId || this.lowerJawFile) {
          this.isSelectedJaw = "lower";
        } else if (this.upperFileId || this.upperJawFile) {
          this.isSelectedJaw = "upper";
        }
      }
      requestAnimationFrame(() => {
        this.$store.commit("showLoader", false);
      });
    } catch (err) {
      console.error(err);
      this.$store.commit("showLoader", false);
    }
  },
  beforeDestroy() {
    // Remove event listeners
    window.removeEventListener("resize", this._onWindowResizeLower);
    window.removeEventListener("resize", this._onWindowResizeUpper);
    window.removeEventListener("keydown", this._handleKeyDown);
    window.removeEventListener("keyup", this._handleKeyUp);

    // Dispose controls & renderers
    if (this._rafLower) cancelAnimationFrame(this._rafLower);
    if (this._rafUpper) cancelAnimationFrame(this._rafUpper);

    if (this._orbitControls) this._orbitControls.dispose();
    if (this._trackballControls) this._trackballControls.dispose();
    if (this._flyControls) this._flyControls.dispose();
    if (this._transformControlLower) this._transformControlLower.dispose();

    if (this._orbitControlsUpper) this._orbitControlsUpper.dispose();
    if (this._trackballControlsUpper) this._trackballControlsUpper.dispose();
    if (this._flyControlsUpper) this._flyControlsUpper.dispose();
    if (this._transformControlUpper) this._transformControlUpper.dispose();

    if (this._renderer) {
      this._renderer.dispose();
      if (this._renderer.forceContextLoss) this._renderer.forceContextLoss();
      this._renderer.domElement = null;
      this._renderer.context = null;
      this._renderer = null;
    }
    if (this._rendererUpper) {
      this._rendererUpper.dispose();
      if (this._rendererUpper.forceContextLoss) this._rendererUpper.forceContextLoss();
      this._rendererUpper.domElement = null;
      this._rendererUpper.context = null;
      this._rendererUpper = null;
    }
  },
  methods: {
      _removeAndDisposeFrom(group, obj) {
      if (!obj) return;
      this._disposeObject3D(obj);
      try { group.remove(obj); } catch(e){}
    },
    _clearGroup(group) {
      if (!group) return;
      const toRemove = [...group.children];
      toRemove.forEach(c => {
        this._disposeObject3D(c);
        group.remove(c);
      });
    },

    _disposeObject3D(obj) {
      if (!obj) return;
      obj.traverse((child) => {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => {
              if (m.map) m.map.dispose();
              m.dispose();
            });
          } else {
            if (child.material.map) child.material.map.dispose();
            child.material.dispose();
          }
        }
      });
    },
    async reloadFiles() {
      // clean previous user/ground-truth models if needed
      this._removeAndDisposeFrom(this._userGroupUpper, this._userModelUpper);
      this._removeAndDisposeFrom(this._userGroupLower, this._userModelLower);
      this._removeAndDisposeFrom(this._userGroupUpper, this._upperGroundTruthModel);
      this._removeAndDisposeFrom(this._userGroupLower, this._lowerGroundTruthModel);
      this._userModelUpper = this._userModelLower = null;
      this._upperGroundTruthModel = this._lowerGroundTruthModel = null;
      this._clearGroup(this._biteGroup);

      if (this._transformControlUpper) {
        try { this._userGroupUpper.remove(this._transformControlUpper); } catch(e){}
        this._transformControlUpper.dispose();
        this._transformControlUpper = null;
      }
      if (this._transformControlLower) {
        try { this._sceneLower.remove(this._transformControlLower); } catch(e){}
        this._transformControlLower.dispose();
        this._transformControlLower = null;
      }
      await this._loadFiles();
    },
    updateCase() {
       this.$emit('updateCase');
    },
    openTeethFilePicker() {
      this.$refs.fileTeethInput.click();
    },
    async previewTeeth(input) {
      let file;

      if (input?.target?.files) {
        // Called from file input
        file = input.target.files[0];
      } else if (input instanceof File) {
        // Called directly with file from drag and drop
        file = input;
      } else {
        console.warn("No file detected");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      this.$store.commit("showLoader", true);

      await this.$store
        .dispatch("caseLists/parseFile", formData)
        .then(async (res) => {
          this.selectedTeeth = res.data.markedTeeth.map((tooth) => {
            const match = this.palette.find(
              (p) => p.reconstructionType === tooth.reconstructionType
            );
            return {
              color: match ? match.color : "#000000",
              tooth: String(tooth.number),
            };
          });
        })
        .finally(() => {
          this.$store.commit("showLoader", false);
        });
    },
    downloadFiles(array) {
      FileService.downloadFiles(array, this.$store);
    },
    /**
     * Download the upper jaw if the prop-based file ID is present
     */
    downloadUpperJaw() {
      if (!this.downloadUpper) return;
      this.downloadFiles([this.downloadUpper]);
    },
    /**
     * Download the lower jaw if the prop-based file ID is present
     */
    downloadLowerJaw() {
      if (!this.downloadLower) return;
      this.downloadFiles([this.downloadLower]);
    },
    downloadTeethFile() {
      if (!this.teethFile) return;
      this.downloadFiles([this.teethFile]);
    },
    // -----------------------------------------------
    //  LOAD COSTS & GROUND TRUTH
    // -----------------------------------------------
    async _loadFiles() {
      const response = await this.$store.dispatch("caseLists/getCreditCosts");
      const upperExtension = response?.data?.data?.upperExtension ?? "stl";
      const lowerExtension = response?.data?.data?.lowerExtension ?? "stl";
      const upperId = response?.data?.data?.upperJawTemplate ?? "";
      const lowerId = response?.data?.data?.lowerJawTemplate ?? "";
      let upperJawFile, lowerJawFile;
      if (upperId) {
        upperJawFile = await FileService.getFileById(upperId);
      }
      if (lowerId) {
        lowerJawFile = await FileService.getFileById(lowerId);
      }

      // In bite mode, we skip the upper template entirely
      if (!this.biteScanned && upperJawFile) {
        this.isSelectedJaw = "upper";
        await this._previewGroundTruthModel(
          upperJawFile,
          upperExtension,
          this._userGroupUpper,
          "_upperGroundTruthModel"
        );
      } else {
        this.isSelectedJaw = "lower";
      }

      if (lowerJawFile) {
        await this._previewGroundTruthModel(
          lowerJawFile,
          lowerExtension,
          this._userGroupLower,
          "_lowerGroundTruthModel"
        );
      }
      if (this.upperFileId) {
        this.isSelectedJaw = this.biteScanned ? "lower" : "upper";
        await this._loadFileAndRender(
          this.upperFileId,
          this.biteScanned ? this._biteGroup : this._userGroupUpper,
          "upper",
          "_userModelUpper",
          this.biteScanned ? this._cameraLower : this._cameraUpper,
          this.biteScanned ? this._renderer : this._rendererUpper,
          this.biteScanned ? this._currentLowerControls : this._currentUpperControls
        );
      } else {
        this.isSelectedJaw = "lower";
      }
      if (this.lowerFileId) {
        this.isSelectedJaw = this.isSelectedJaw || "lower";
        await this._loadFileAndRender(
          this.lowerFileId,
          this.biteScanned ? this._biteGroup : this._userGroupLower,
          "lower",
          "_userModelLower",
          this._cameraLower,
          this._renderer,
          this._currentLowerControls
        );
      }
    },
    async _previewGroundTruthModel(fileObj, extension, group, modelProp) {
      if (!fileObj || !fileObj.data) return;
      const arrayBuf = await fileObj.data.arrayBuffer();
      const newModel = loadModel(arrayBuf, extension, {
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        color: 0x05fffa,
      });
      // Remove old if any
      if (this[modelProp]) {
        this._disposeObject3D(this[modelProp]);
        group.remove(this[modelProp]);
      }
      group.add(newModel);
      this[modelProp] = newModel; // store reference
    },

    async _loadFileAndRender(
      fileId,
      group,
      jawName,
      modelProp,
      camera,
      renderer,
      controls
    ) {
      try {
        // 1) Fetch file
        const fileObj = await FileService.getFileById(fileId);
        const inBite = this.biteScanned || (group === this._biteGroup);

        this[jawName] = {};
        this[jawName].fileName = fileObj.name;

        // 2) Convert to ArrayBuffer
        const arrayBuf = await fileObj.data.arrayBuffer();

        // 3) Derive extension
        const extension = fileObj.name?.split(".").pop().toLowerCase() || "stl";

        // 4) Remove old model if exists
        if (this[modelProp]) {
          this._removeAndDisposeFrom(group, this[modelProp]);
          this[modelProp] = null;
        }

        // 5) Load new model
        const newModel = loadModel(arrayBuf, extension, {
          side: THREE.DoubleSide,
          color: 0xb1bcbc,
        });
        group.add(newModel);
        this[modelProp] = newModel;

        // 6) Transform controls (bite vs non-bite)
        if (inBite) {
          // Single TransformControls in LOWER scene for both jaws
          if (!this._transformControlLower) {
            this._transformControlLower = new TransformControls(
              this._cameraLower,
              this._renderer.domElement
            );
            this._sceneLower.add(this._transformControlLower);

            // listeners only once
            this._transformControlLower.addEventListener("dragging-changed", (evt) => {
              this._currentLowerControls.enabled = !evt.value;
              if (evt.value) {
                if (this._userModelUpper) this._startUpperMatrix = this._userModelUpper.matrix.clone();
                if (this._userModelLower) this._startLowerMatrix = this._userModelLower.matrix.clone();
              }
            });

            this._transformControlLower.addEventListener("change", () => {
              // keep both jaws synced: apply Δ to the other jaw
              if (!(this._userModelUpper && this._userModelLower)) return;

              const src = (this._transformControlLower.object === this._userModelUpper)
                ? this._userModelUpper
                : this._userModelLower;
              const other = (src === this._userModelUpper)
                ? this._userModelLower
                : this._userModelUpper;

              const startSrc = (src === this._userModelUpper)
                ? this._startUpperMatrix
                : this._startLowerMatrix;
              const startOther = (src === this._userModelUpper)
                ? this._startLowerMatrix
                : this._startUpperMatrix;

              if (!startSrc || !startOther) return;

              src.updateMatrix();
              const delta = src.matrix.clone().multiply(startSrc.clone().invert());
              const newOther = delta.clone().multiply(startOther);

              const p = new THREE.Vector3();
              const q = new THREE.Quaternion();
              const s = new THREE.Vector3();
              newOther.decompose(p, q, s);

              other.position.copy(p);
              other.quaternion.copy(q);
              other.scale.copy(s);
              other.updateMatrix();
              other.updateMatrixWorld();
            });
          }

          // Attach to the jaw we just loaded
          this._transformControlLower.attach(newModel);
        } else {
          // Non-bite mode: one TransformControls per scene/jaw
          const transformControlKey =
            modelProp === "_userModelUpper" ? "_transformControlUpper" : "_transformControlLower";

          if (this[transformControlKey]) {
            try { group.remove(this[transformControlKey]); } catch (e) {}
            this[transformControlKey].dispose();
            this[transformControlKey] = null;
          }

          const tc = new TransformControls(camera, renderer.domElement);
          tc.attach(newModel);
          group.add(tc);
          this[transformControlKey] = tc;

          tc.addEventListener("dragging-changed", (evt) => {
            const ctrl = (transformControlKey === "_transformControlUpper")
              ? this._currentUpperControls
              : this._currentLowerControls;
            ctrl.enabled = !evt.value;
          });
        }

        // NOTE: no stray references to "tControls" below this line.
        // The render loops already repaint; manual renderer.render(...) is not required here.

      } catch (err) {
        console.error("Failed to load file by ID: ", fileId, err);
      }
    },


    // -----------------------------------------------
    //  INIT LOWER SCENE
    // -----------------------------------------------
    _initLowerScene() {
      this._sceneLower = new THREE.Scene();
      addLights(this._sceneLower);

      const card = this.$refs.card;
      const w = card.clientWidth,
        h = card.clientHeight;

      this._cameraLower = new THREE.PerspectiveCamera(60, w / h, 0.1, 1000);
      this._cameraLower.position.set(0, 0, 100);

      this._renderer = new THREE.WebGLRenderer({
        antialias: true,
        canvas: document.getElementById("canvas"),
      });
      this._renderer.setSize(w, h);

      // Controls
      this._orbitControls = new OrbitControls(
        this._cameraLower,
        this._renderer.domElement
      );
      this._trackballControls = new TrackballControls(
        this._cameraLower,
        this._renderer.domElement
      );
      this._flyControls = new FlyControls(
        this._cameraLower,
        this._renderer.domElement
      );
      this._flyControls.movementSpeed = 10;
      this._flyControls.rollSpeed = Math.PI / 24;
      this._flyControls.dragToLook = true;
      this._flyControls.autoForward = false;
      this._flyControls.enabled = false;

      this._trackballControls.screen.height = h;
      this._trackballControls.screen.width = w;
      this._trackballControls.rotateSpeed = 2;
      this._currentLowerControls = this._trackballControls;
      this._trackballControls.enabled = true;

      // Apply default sensitivity
      this._updateControlsSensitivity(
        {
          orbitControls: this._orbitControls,
          trackballControls: this._trackballControls,
          flyControls: this._flyControls,
        },
        2
      );

      // User group
      this._userGroupLower = new THREE.Group();
      this._sceneLower.add(this._userGroupLower);

      // When biteScanned, we'll render both jaws into this one group in the LOWER scene
      this._biteGroup = new THREE.Group();
      this._sceneLower.add(this._biteGroup);

      // By default, trackball control for lower
      this.switchControls();
    },
    // -----------------------------------------------
    //  INIT UPPER SCENE
    // -----------------------------------------------
    _initUpperScene() {
      this._sceneUpper = new THREE.Scene();
      addLights(this._sceneUpper);

      const cardUpper = this.$refs.cardUpper;
      const w = cardUpper.clientWidth,
        h = cardUpper.clientHeight;

      this._cameraUpper = new THREE.PerspectiveCamera(60, w / h, 0.1, 1000);
      this._cameraUpper.position.set(0, 0, 100);

      this._rendererUpper = new THREE.WebGLRenderer({
        antialias: true,
        canvas: document.getElementById("canvasUpper"),
      });
      this._rendererUpper.setSize(w, h);

      // Controls
      this._orbitControlsUpper = new OrbitControls(
        this._cameraUpper,
        this._rendererUpper.domElement
      );
      this._trackballControlsUpper = new TrackballControls(
        this._cameraUpper,
        this._rendererUpper.domElement
      );
      this._flyControlsUpper = new FlyControls(
        this._cameraUpper,
        this._rendererUpper.domElement
      );
      this._flyControlsUpper.movementSpeed = 10;
      this._flyControlsUpper.rollSpeed = Math.PI / 24;
      this._flyControlsUpper.dragToLook = true;
      this._flyControlsUpper.autoForward = false;
      this._flyControlsUpper.enabled = false;

      this._trackballControlsUpper.screen.height = h;
      this._trackballControlsUpper.screen.width = w;
      this._trackballControlsUpper.rotateSpeed = 2;
      this._currentUpperControls = this._trackballControlsUpper;
      this._trackballControlsUpper.enabled = true;

      this._updateControlsSensitivity(
        {
          orbitControls: this._orbitControlsUpper,
          trackballControls: this._trackballControlsUpper,
          flyControls: this._flyControlsUpper,
        },
        2
      );

      // User group
      this._userGroupUpper = new THREE.Group();
      this._sceneUpper.add(this._userGroupUpper);

      this.switchControlsUpper();
    },

    // -----------------------------------------------
    //  ANIMATION LOOPS
    // -----------------------------------------------
    _animateLower() {
      this._rafLower = requestAnimationFrame(() => this._animateLower());
      if (!this._clock) return; // in case of destroy

      const delta = this._clock.getDelta();
      if (this._currentLowerControls === this._flyControls) {
        this._flyControls.update(delta);
      } else {
        this._currentLowerControls.update();
      }
      this._renderer.render(this._sceneLower, this._cameraLower);
    },
    _animateUpper() {
      this._rafUpper = requestAnimationFrame(() => this._animateUpper());
      if (!this._clockUpper) return;

      const delta = this._clockUpper.getDelta();
      if (this._currentUpperControls === this._flyControlsUpper) {
        this._flyControlsUpper.update(delta);
      } else {
        this._currentUpperControls.update();
      }
      this._rendererUpper.render(this._sceneUpper, this._cameraUpper);
    },

    _onWindowResizeLower() {
      const card = this.$refs.card;
      if (!card) return;
      const w = card.clientWidth,
        h = card.clientHeight;
      if (this._cameraLower) {
        this._cameraLower.aspect = w / h;
        this._cameraLower.updateProjectionMatrix();
      }
      if (this._renderer) {
        this._renderer.setSize(w, h);
      }
    },
    _onWindowResizeUpper() {
      const cardUpper = this.$refs.cardUpper;
      if (!cardUpper) return;
      const w = cardUpper.clientWidth,
        h = cardUpper.clientHeight;
      if (this._cameraUpper) {
        this._cameraUpper.aspect = w / h;
        this._cameraUpper.updateProjectionMatrix();
      }
      if (this._rendererUpper) {
        this._rendererUpper.setSize(w, h);
      }
    },

    // -----------------------------------------------
    //  CONTROL SWITCH
    // -----------------------------------------------
    switchControls() {
      // For lower scene
      this._orbitControls.enabled = false;
      this._trackballControls.enabled = false;
      this._flyControls.enabled = false;

      switch (this.selectedControl) {
        case "orbit":
          this._orbitControls.enabled = true;
          this._currentLowerControls = this._orbitControls;
          break;
        case "trackball":
          this._trackballControls.enabled = true;
          this._currentLowerControls = this._trackballControls;
          break;
        case "fly":
          this._flyControls.enabled = true;
          this._currentLowerControls = this._flyControls;
          break;
      }
    },
    switchControlsUpper() {
      // For upper scene
      this._orbitControlsUpper.enabled = false;
      this._trackballControlsUpper.enabled = false;
      this._flyControlsUpper.enabled = false;

      switch (this.selectedControlUpper) {
        case "orbit":
          this._orbitControlsUpper.enabled = true;
          this._currentUpperControls = this._orbitControlsUpper;
          break;
        case "trackball":
          this._trackballControlsUpper.enabled = true;
          this._currentUpperControls = this._trackballControlsUpper;
          break;
        case "fly":
          this._flyControlsUpper.enabled = true;
          this._currentUpperControls = this._flyControlsUpper;
          break;
      }
    },
    _updateControlsSensitivity(ctrlSet, sensitivity) {
      ctrlSet.orbitControls.rotateSpeed = sensitivity;
      ctrlSet.orbitControls.zoomSpeed = sensitivity;
      ctrlSet.orbitControls.panSpeed = sensitivity / 2;

      ctrlSet.trackballControls.rotateSpeed = sensitivity;
      ctrlSet.trackballControls.zoomSpeed = sensitivity;
      ctrlSet.trackballControls.panSpeed = sensitivity / 2;

      ctrlSet.flyControls.movementSpeed = sensitivity * 10;
      ctrlSet.flyControls.rollSpeed = (sensitivity * Math.PI) / 24;
    },

    // -----------------------------------------------
    //  TRANSFORM (ROTATE/TRANSLATE)
    // -----------------------------------------------
    rotateBtn(jawType) {
      if (this.biteScanned) {
        if (this._transformControlUpper) this._transformControlUpper.setMode("rotate");
        if (this._transformControlLower) this._transformControlLower.setMode("rotate");
      } else if (jawType === "upper") {
        if (this._transformControlUpper) {
          this._transformControlUpper.setMode("rotate");
        }
      } else {
        if (this._transformControlLower) {
          this._transformControlLower.setMode("rotate");
        }
      }
    },
    _isNearIdentity(m, eps = 1e-4) {
      // assumes m is a THREE.Matrix4
      const e = m.elements;
      // diag ~ 1
      if (Math.abs(e[0]-1)>eps || Math.abs(e[5]-1)>eps || Math.abs(e[10]-1)>eps || Math.abs(e[15]-1)>eps) return false;
      // off-diag ~ 0
      const idx = [1,2,3,4,6,7,8,9,11,12,13,14];
      for (const i of idx) if (Math.abs(e[i])>eps) return false;
      return true;
    },
    translateBtn(jawType) {
        if (this.biteScanned) {
          if (this._transformControlUpper) this._transformControlUpper.setMode("translate");
          if (this._transformControlLower) this._transformControlLower.setMode("translate");
        } else if (jawType === "upper") {
        if (this._transformControlUpper) {
          this._transformControlUpper.setMode("translate");
        }
      } else {
        if (this._transformControlLower) {
          this._transformControlLower.setMode("translate");
        }
      }
    },
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = (err) => reject(err);
        reader.readAsArrayBuffer(file);
      });
    },

    async previewJaw(jawType, event) {
      let scheduleRaf = false;
      try {
        this.$store.commit("showLoader", true);

        const file = event?.target?.files?.[0];
        if (!file) return;

        const maxSizeInMB = 100;
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
          NotificationService.showError("File size should not exceed 100 MB.");
          if (event?.target) event.target.value = "";
          return;
        }

        // Remember which jaw we’re working with
        if (jawType === "upper") {
          this.upperJawFile = file;
          if (!this.biteScanned) this.isSelectedJaw = "upper";
        } else {
          this.lowerJawFile = file;
          if (!this.biteScanned) this.isSelectedJaw = "lower";
        }

        const inBite = !!this.biteScanned;

        // Choose the target group where the mesh lives
        const targetGroup = inBite
          ? this._biteGroup
          : jawType === "upper"
            ? this._userGroupUpper
            : this._userGroupLower;

        // Remove any previous model (from the correct group)
        if (jawType === "upper" && this._userModelUpper) {
          this._disposeObject3D(this._userModelUpper);
          try { targetGroup.remove(this._userModelUpper); } catch {}
          this._userModelUpper = null;
          if (!inBite) {
            this.upperMatrixIn = null;
            this.upperMatrixWorldIn = null;
          }
        }
        if (jawType === "lower" && this._userModelLower) {
          this._disposeObject3D(this._userModelLower);
          try { targetGroup.remove(this._userModelLower); } catch {}
          this._userModelLower = null;
          if (!inBite) {
            this.lowerMatrixIn = null;
            this.lowerMatrixWorldIn = null;
          }
        }

        // Read + load the model
        const arrayBuf = await this.readFileAsArrayBuffer(file);
        const extension = (file.name.split(".").pop() || "stl").toLowerCase();
        const newModel = loadModel(arrayBuf, extension, {
          side: THREE.DoubleSide,
          color: 0xb1bcbc,
        });

        targetGroup.add(newModel);
        if (jawType === "upper") this._userModelUpper = newModel;
        else this._userModelLower = newModel;

        console.log("inBite: ",inBite);
        console.log("this.upperMatrixIn: ",this.upperMatrixIn);


        // Initialize placement using stored registration matrix (M_reg)
        // and, if bite is aligned, also apply current viewer delta (Δ) from the other jaw:
        // finalMatrix = Δ * M_reg  (same composition used when saving: premultiply)
        {
          const has16 = (a) => Array.isArray(a) && a.length >= 16;
          const toM4  = (a) => new THREE.Matrix4().fromArray(a.slice(0,16)); // strip Vue __ob__

          // Prefer WORLD, fall back to LOCAL
          const ownLocal = jawType === "upper" ? this.upperMatrixIn        : this.lowerMatrixIn;
          const ownWorld = jawType === "upper" ? this.upperMatrixWorldIn    : this.lowerMatrixWorldIn;
          const othLocal = jawType === "upper" ? this.lowerMatrixIn        : this.upperMatrixIn;
          const othWorld = jawType === "upper" ? this.lowerMatrixWorldIn    : this.upperMatrixWorldIn;

          // We’ll treat matrices as world-space targets
          const M_reg_this = has16(ownWorld) ? toM4(ownWorld) :
                            has16(ownLocal) ? toM4(ownLocal) :
                            new THREE.Matrix4(); // identity fallback

          let Delta = new THREE.Matrix4(); // identity by default

          if (inBite) {
            const other = jawType === "upper" ? this._userModelLower : this._userModelUpper;
            if (other) {
              // Use world matrices to be safe
              other.updateMatrixWorld(true);
              const currentOther = other.matrixWorld.clone();

              const M_reg_other = has16(othWorld) ? toM4(othWorld) :
                                  has16(othLocal) ? toM4(othLocal) :
                                  null;

              // If the other jaw looks identity-ish (typical when geometry is already in registered space),
              // treat its baseline as identity => no delta.
              // Otherwise, compare against its stored registration matrix.
              const baselineOther = (M_reg_other && !this._isNearIdentity(currentOther))
                ? M_reg_other
                : new THREE.Matrix4(); // identity baseline

              Delta.copy(currentOther).multiply(baselineOther.clone().invert());
            }
          }

          // final world matrix for the new upload
          const finalM = Delta.clone().multiply(M_reg_this);

          console.log('inBite:', inBite);
          console.log('M_reg_this (used):', M_reg_this.elements);
          console.log('Delta (used):', Delta.elements);

          const p = new THREE.Vector3();
          const q = new THREE.Quaternion();
          const s = new THREE.Vector3();
          finalM.decompose(p, q, s);

          newModel.position.copy(p);
          newModel.quaternion.copy(q);
          newModel.scale.copy(s);
          newModel.updateMatrix();
          newModel.updateMatrixWorld(true);
        }


        // -------- TransformControls --------
        if (inBite) {
          // One control in LOWER scene for both jaws
          if (!this._transformControlLower) {
            const tc = new TransformControls(this._cameraLower, this._renderer.domElement);
            this._sceneLower.add(tc);
            this._transformControlLower = tc;

            // listeners once
            tc.addEventListener("dragging-changed", (evt) => {
              this._currentLowerControls.enabled = !evt.value;
              if (evt.value) {
                if (this._userModelUpper) this._startUpperMatrix = this._userModelUpper.matrix.clone();
                if (this._userModelLower) this._startLowerMatrix = this._userModelLower.matrix.clone();
              }
            });

            tc.addEventListener("change", () => {
              if (!(this._userModelUpper && this._userModelLower)) return;

              const src   = (tc.object === this._userModelUpper) ? this._userModelUpper : this._userModelLower;
              const other = (src === this._userModelUpper) ? this._userModelLower : this._userModelUpper;

              const startSrc   = (src === this._userModelUpper) ? this._startUpperMatrix : this._startLowerMatrix;
              const startOther = (src === this._userModelUpper) ? this._startLowerMatrix : this._startUpperMatrix;
              if (!startSrc || !startOther) return;

              src.updateMatrix();
              const delta = src.matrix.clone().multiply(startSrc.clone().invert());
              const newOther = delta.clone().multiply(startOther);

              const p = new THREE.Vector3(), q = new THREE.Quaternion(), s = new THREE.Vector3();
              newOther.decompose(p, q, s);
              other.position.copy(p);
              other.quaternion.copy(q);
              other.scale.copy(s);
              other.updateMatrix();
              other.updateMatrixWorld();
            });
          }

          this._transformControlLower.attach(newModel);
          this._currentLowerControls.enabled = true; // make sure nav stays alive

          // make absolutely sure the lower canvas receives input
          this.$nextTick(() => {
            if (this.$refs.card)      this.$refs.card.style.pointerEvents = "auto";
            if (this.$refs.cardUpper) this.$refs.cardUpper.style.pointerEvents = "none";
          });

        } else {
          // Non-bite: separate controls per scene/jaw — attach to SCENE
          if (jawType === "upper") {
            // dispose old TC from upper scene
            if (this._transformControlUpper) {
              try { this._sceneUpper.remove(this._transformControlUpper); } catch {}
              this._transformControlUpper.dispose();
              this._transformControlUpper = null;
            }

            const tc = new TransformControls(this._cameraUpper, this._rendererUpper.domElement);
            this._sceneUpper.add(tc);
            tc.attach(newModel);
            this._transformControlUpper = tc;

            tc.addEventListener("dragging-changed", (evt) => {
              this._currentUpperControls.enabled = !evt.value;
            });

            // ensure nav works right away
            this._currentUpperControls.enabled = true;

          } else {
            // dispose old TC from lower scene
            if (this._transformControlLower) {
              try { this._sceneLower.remove(this._transformControlLower); } catch {}
              this._transformControlLower.dispose();
              this._transformControlLower = null;
            }

            const tc = new TransformControls(this._cameraLower, this._renderer.domElement);
            this._sceneLower.add(tc);
            tc.attach(newModel);
            this._transformControlLower = tc;

            tc.addEventListener("dragging-changed", (evt) => {
              this._currentLowerControls.enabled = !evt.value;
            });

            this._currentLowerControls.enabled = true;
          }
        }

        // Make sure the right canvas receives events
        if (!inBite) {
          // Only one canvas is visible in non-bite mode
          if (jawType === "upper") {
            this._rendererUpper.domElement.style.pointerEvents = "auto";
            this._renderer.domElement.style.pointerEvents = "none";
          } else {
            this._rendererUpper.domElement.style.pointerEvents = "none";
            this._renderer.domElement.style.pointerEvents = "auto";
          }
        }

        scheduleRaf = true;
        requestAnimationFrame(() => {
          this.$store.commit("showLoader", false);
        });
      } catch (e) {
        console.error("Error in previewJaw:", e);
      } finally {
        if (!scheduleRaf) {
          this.$store.commit("showLoader", false);
        }
      }
    },


    _centerObject(obj) {
      const box = new THREE.Box3().setFromObject(obj);
      const center = box.getCenter(new THREE.Vector3());
      obj.position.sub(center);
    },
    deleteUpperJawFile() {
      this.upperJawFile = null;
      if (this._userModelUpper) {
        this._userGroupUpper.remove(this._userModelUpper);
        this._userModelUpper = null;
        this.upperMatrixIn = null;
        this.upperMatrixWorldIn = null;
      }
    },
    deleteLowerJawFile() {
      this.lowerJawFile = null;
      if (this._userModelLower) {
        this._userGroupLower.remove(this._userModelLower);
        this._userModelLower = null;
        this.lowerMatrixIn = null;
        this.lowerMatrixWorldIn = null;
      }
    },
    openUpperJawFilePicker() {
      this.$refs.fileUpperJawInput.click();
    },
    openLowerJawFilePicker() {
      this.$refs.fileLowerJawInput.click();
    },

    // -----------------------------------------------
    //  KEYBOARD SHORTCUTS
    // -----------------------------------------------
    _handleKeyDown(event) {
      let ctrlKey = navigator.userAgent.includes("Mac") ? "Meta" : "Control";
      if (event.key === ctrlKey) {
        if (this._transformControlUpper) {
          this._transformControlUpper.setMode("rotate");
        }
        if (this._transformControlLower) {
          this._transformControlLower.setMode("rotate");
        }
      }
    },
    _handleKeyUp(event) {
      let ctrlKey = navigator.userAgent.includes("Mac") ? "Meta" : "Control";
      if (event.key === ctrlKey) {
        if (this._transformControlUpper) {
          this._transformControlUpper.setMode("translate");
        }
        if (this._transformControlLower) {
          this._transformControlLower.setMode("translate");
        }
      }
    },

    // -----------------------------------------------
    //  DRAG & DROP
    // -----------------------------------------------
    onDragOver() {
      this.isDragOver = true;
    },
    onDragLeave() {
      this.isDragOver = false;
    },
    onDrop(event, jawType) {
      this.isDragOver = false;
      const droppedFiles = event.dataTransfer.files;

      if (droppedFiles && droppedFiles.length > 0) {
        if (jawType === "teeth") {
          const file = droppedFiles[0];
          this.previewTeeth(file);
          return;
        }
        const allowedExtensions = ["stl", "obj", "ply"];
        const filteredFiles = Array.from(droppedFiles).filter((file) => {
          const extension = file.name.split(".").pop().toLowerCase();
          return allowedExtensions.includes(extension);
        });

        if (filteredFiles.length === 0) {
          NotificationService.showError(
            "Only STL, OBJ, and PLY files are allowed."
          );
          return;
        }

        this.previewJaw(jawType, { target: { files: filteredFiles } });
      }
    },

    // -----------------------------------------------
    //  RELOAD & UPLOAD
    // -----------------------------------------------
    reloadPage() {
      window.location.reload();
    },

    async uploadData2DTo3D() {
      try {
        if (this._userModelUpper) {
          let upperMatrix =
            this.upperMatrixIn && this.upperMatrixIn.length === 16
              ? new THREE.Matrix4().fromArray(this.upperMatrixIn)
              : new THREE.Matrix4();

          let upperMatrixWorld =
            this.upperMatrixWorldIn && this.upperMatrixWorldIn.length === 16
              ? new THREE.Matrix4().fromArray(this.upperMatrixWorldIn)
              : new THREE.Matrix4();

          // make sure your model has updated its own matrix/worldMatrix
          this._userModelUpper.updateMatrix();
          this._userModelUpper.updateMatrixWorld();

          // --- build the *local* file matrix ----------------------------------------
          const regLocal = upperMatrix
            .clone() // keep the original intact
            .premultiply(this._userModelUpper.matrix); // Δ * Mreg  (NOT Mreg * Δ)

          this.upperJawFileMatrix = {
            orig: [],
            reg: regLocal.elements.slice(), // column-major, indices 12-14 are x,y,z
          };

          // --- build the *world* matrix --------------------------------------------
          const regWorld = upperMatrixWorld
            .clone()
            .premultiply(this._userModelUpper.matrixWorld);

          this.upperJawFileMatrixWorld = {
            orig: [],
            reg: regWorld.elements.slice(),
          };

          this.upperJawFileRotation.reg.x = this._userModelUpper.rotation.x;
          this.upperJawFileRotation.reg.y = this._userModelUpper.rotation.y;
          this.upperJawFileRotation.reg.z = this._userModelUpper.rotation.z;

          this.upperJawFileTranslation.reg.x = this._userModelUpper.position.x;
          this.upperJawFileTranslation.reg.y = this._userModelUpper.position.y;
          this.upperJawFileTranslation.reg.z = this._userModelUpper.position.z;

          this.upperJawFileQuaternion.reg.x = this._userModelUpper.quaternion.x;
          this.upperJawFileQuaternion.reg.y = this._userModelUpper.quaternion.y;
          this.upperJawFileQuaternion.reg.z = this._userModelUpper.quaternion.z;
          this.upperJawFileQuaternion.reg.w = this._userModelUpper.quaternion.w;

          this.upperJawFileScale.reg.x = this._userModelUpper.scale.x;
          this.upperJawFileScale.reg.y = this._userModelUpper.scale.y;
          this.upperJawFileScale.reg.z = this._userModelUpper.scale.z;
        }

        if (this._userModelLower) {
          //--------------------------------------------------------------------------
          // 1. make sure the model’s matrices are current
          //--------------------------------------------------------------------------
          this._userModelLower.updateMatrix();
          this._userModelLower.updateMatrixWorld();

          //--------------------------------------------------------------------------
          // 2. parse the stored registration matrices – no transpose anymore
          //--------------------------------------------------------------------------
          const lowerMatrix = // local (file) space
            this.lowerMatrixIn && this.lowerMatrixIn.length
              ? new THREE.Matrix4().fromArray(this.lowerMatrixIn)
              : new THREE.Matrix4();

          const lowerMatrixWorld = // world space
            this.lowerMatrixWorldIn && this.lowerMatrixWorldIn.length
              ? new THREE.Matrix4().fromArray(this.lowerMatrixWorldIn)
              : new THREE.Matrix4();

          //--------------------------------------------------------------------------
          // 3. compose NEW = Δ * M   (premultiply!)
          //--------------------------------------------------------------------------
          const regLocal = lowerMatrix
            .clone() // keep the original intact
            .premultiply(this._userModelLower.matrix); // Δ * M

          this.lowerJawFileMatrix = {
            orig: [],
            reg: regLocal.elements.slice(), // column-major, 12-14 hold x,y,z
          };

          const regWorld = lowerMatrixWorld
            .clone()
            .premultiply(this._userModelLower.matrixWorld); // Δ * M (world)

          this.lowerJawFileMatrixWorld = {
            orig: [],
            reg: regWorld.elements.slice(),
          };

          this.lowerJawFileRotation.reg.x = this._userModelLower.rotation.x;
          this.lowerJawFileRotation.reg.y = this._userModelLower.rotation.y;
          this.lowerJawFileRotation.reg.z = this._userModelLower.rotation.z;

          this.lowerJawFileTranslation.reg.x = this._userModelLower.position.x;
          this.lowerJawFileTranslation.reg.y = this._userModelLower.position.y;
          this.lowerJawFileTranslation.reg.z = this._userModelLower.position.z;

          this.lowerJawFileQuaternion.reg.x = this._userModelLower.quaternion.x;
          this.lowerJawFileQuaternion.reg.y = this._userModelLower.quaternion.y;
          this.lowerJawFileQuaternion.reg.z = this._userModelLower.quaternion.z;
          this.lowerJawFileQuaternion.reg.w = this._userModelLower.quaternion.w;

          this.lowerJawFileScale.reg.x = this._userModelLower.scale.x;
          this.lowerJawFileScale.reg.y = this._userModelLower.scale.y;
          this.lowerJawFileScale.reg.z = this._userModelLower.scale.z;
        }


        // If bite was scanned, normalize: send the same transform for upper & lower
        if (this.biteScanned && this._userModelLower) {
          // Prefer lower as the single source of truth
          if (this.lowerJawFileMatrix?.reg) {
            const sameLocal = [...this.lowerJawFileMatrix.reg];
            this.upperJawFileMatrix = { reg: [...sameLocal] };
          }
          if (this.lowerJawFileMatrixWorld?.reg) {
            const sameWorld = [...this.lowerJawFileMatrixWorld.reg];
            this.upperJawFileMatrixWorld = { reg: [...sameWorld] };
          }
          this.upperJawFileRotation    = this.lowerJawFileRotation;
          this.upperJawFileTranslation = this.lowerJawFileTranslation;
          this.upperJawFileQuaternion  = this.lowerJawFileQuaternion;
          this.upperJawFileScale       = this.lowerJawFileScale;
        }

        let upperId = null;
        let lowerId = null;
        const uploadPromises = [];
        let exporter = new STLExporter();

        if (this._userModelUpper) {
          // Export ASCII
          const asciiStl = exporter.parse(this._userModelUpper);
          const binaryData = this.convertASCIIToBinarySTL(asciiStl);

          // Create file
          let blob = new Blob([binaryData], {
            type: "application/octet-stream",
          });
          let fileUpper = new File(
            [blob],
            this.upperJawFile?.name ?? "upper_input.stl",
            {
              type: "application/octet-stream",
            }
          );
          // Upload, TODO: delete old file
          uploadPromises.push(
            await FileService.uploadFile(
              fileUpper,
              { visibility: 0 },
              0,
              0.33,
              this.productName === "dentaBridge"
            ).then((resp) => {
              upperId = resp.data[0];
            })
          );
        }

        if (this._userModelLower) {
          const asciiStl = exporter.parse(this._userModelLower);
          const binaryData = this.convertASCIIToBinarySTL(asciiStl);

          let blob = new Blob([binaryData], {
            type: "application/octet-stream",
          });
          let fileLower = new File(
            [blob],
            this.lowerJawFile?.name ?? "lower_input.stl",
            {
              type: "application/octet-stream",
            }
          );
          // TODO: delete old file
          uploadPromises.push(
            await FileService.uploadFile(
              fileLower,
              { visibility: 0 },
              0.33,
              0.33,
              this.productName === "dentaBridge"
            ).then((resp) => {
              lowerId = resp.data[0];
            })
          );
        }

        await Promise.all(uploadPromises);

        return {
          upperId: upperId,
          lowerId: lowerId,
          upper_rotation: this.upperJawFileRotation,
          lower_rotation: this.lowerJawFileRotation,
          upper_translation: this.upperJawFileTranslation,
          lower_translation: this.lowerJawFileTranslation,
          upper_matrixWorld: this.upperJawFileMatrixWorld,
          lower_matrixWorld: this.lowerJawFileMatrixWorld,
          upper_quaternion: this.upperJawFileQuaternion,
          lower_quaternion: this.lowerJawFileQuaternion,
          upper_scale: this.upperJawFileScale,
          lower_scale: this.lowerJawFileScale,
          upper_matrixLocal: this.upperJawFileMatrix,
          lower_matrixLocal: this.lowerJawFileMatrix,
        };
      } catch (err) {
        console.error(err);
        return {};
      }
    },
    convertASCIIToBinarySTL(asciiData) {
      // Same code from your original for ASCII->Binary
      const lines = asciiData.split("\n");
      const triangles = [];
      let normal = null;
      let vertices = [];

      for (let line of lines) {
        line = line.trim();
        if (line.startsWith("facet normal")) {
          const normalData = line.match(
            /facet normal\s+([\S]+)\s+([\S]+)\s+([\S]+)/
          );
          if (normalData) {
            normal = [
              parseFloat(normalData[1]),
              parseFloat(normalData[2]),
              parseFloat(normalData[3]),
            ];
          }
          vertices = [];
        } else if (line.startsWith("vertex")) {
          const vertexData = line.match(/vertex\s+([\S]+)\s+([\S]+)\s+([\S]+)/);
          if (vertexData) {
            vertices.push([
              parseFloat(vertexData[1]),
              parseFloat(vertexData[2]),
              parseFloat(vertexData[3]),
            ]);
          }
        } else if (line.startsWith("endfacet")) {
          if (normal && vertices.length === 3) {
            triangles.push({ normal, vertices });
          }
          normal = null;
          vertices = [];
        }
      }

      if (triangles.length === 0) {
        throw new Error("No triangles found in the STL file.");
      }

      const triangleCount = triangles.length;
      const totalSize = 80 + 4 + triangleCount * 50;
      const buffer = new ArrayBuffer(totalSize);
      const dataView = new DataView(buffer);
      let offset = 0;

      // 80-byte header
      for (let i = 0; i < 80; i++) {
        dataView.setUint8(offset++, 0);
      }

      // Triangle count
      dataView.setUint32(offset, triangleCount, true);
      offset += 4;

      // Write triangles
      for (const t of triangles) {
        for (const n of t.normal) {
          dataView.setFloat32(offset, n, true);
          offset += 4;
        }
        for (const v of t.vertices) {
          for (const val of v) {
            dataView.setFloat32(offset, val, true);
            offset += 4;
          }
        }
        dataView.setUint16(offset, 0, true);
        offset += 2;
      }
      return buffer;
    },
  },
  beforeRouteEnter(to, from, next) {
    document.body.classList.add("cookie-layout");
    next();
  },
  beforeRouteLeave(to, from, next) {
    document.body.classList.remove("cookie-layout");
    next();
  },
};
</script>

<style>
body.dark-layout .teeth-selector #toothLabels text {
  fill: #fff;
}

body.dark-layout .teeth-selector .palette-btn {
  color: #fff;
}

.lower-card,
.upper-card {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
}

.lower-card.active {
  z-index: 1;
}

.upper-canvas {
  width: 100% !important;
  height: 100% !important;
  position: absolute;
  left: 0;
  top: 0;
}

.pe-none { pointer-events: none; }
</style>
